// LearnCore Frontend - 文言文学习页面

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import HighlightedText, { processHighlights } from '../components/HighlightedText';
import type { ClassicalPassageWithDetails } from '../types';
import {
  BookOpenIcon,
  ArrowLeftIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  LightBulbIcon,
  AcademicCapIcon,
  BeakerIcon,
  BookmarkIcon,
  StarIcon
} from '@heroicons/react/24/outline';

export default function StudyClassicalChinesePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [passage, setPassage] = useState<ClassicalPassageWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (id) {
      loadPassage();
    }
  }, [id]);

  const loadPassage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getClassicalPassage(id!);
      setPassage(response.passage);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取文言文材料失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    try {
      setRetrying(true);
      await apiClient.triggerClassicalExtraction(id!);
      // 重新加载数据
      await loadPassage();
    } catch (err: any) {
      setError(err.response?.data?.error || '重新提取失败');
    } finally {
      setRetrying(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newExpanded = new Set<string>();
      
      // 如果点击的是已展开的栏目，则关闭它
      if (prev.has(sectionId)) {
        return newExpanded; // 返回空Set，关闭所有
      }
      
      // 否则只展开当前点击的栏目
      newExpanded.add(sectionId);
      return newExpanded;
    });
  };

  // 生成条目ID的辅助函数
  const getWordEntryId = (type: string, wordId: string, index: number) => {
    return `${type}-entry-${wordId}-${index}`;
  };

  // 处理高亮词点击
  const handleWordClick = (wordId: string, wordType: string) => {
    // 1. 展开字词精讲栏目
    toggleSection('words');
    
    // 2. 等待DOM更新后滚动到对应条目
    setTimeout(() => {
      const element = document.getElementById(wordId);
      if (element) {
        // 使用 smooth 滚动，并将元素居中显示
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        
        // 添加临时高亮效果
        element.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2');
        }, 2000);
      }
    }, 100); // 给DOM更新留出时间
    
    // 3. 记录点击的词类型（可用于后续分析或功能扩展）
    console.log(`Clicked word type: ${wordType}`);
  };

  // 处理高亮数据
  const processedHighlights = useMemo(() => {
    if (!passage) return [];
    return processHighlights(
      passage.key_words,
      passage.function_words,
      passage.special_chars || [],
      passage.sentence_patterns || [],
      passage.word_usage || [],
      passage.original_text
    );
  }, [passage?.key_words, passage?.function_words, passage?.special_chars, passage?.sentence_patterns, passage?.word_usage, passage?.original_text]);

  // 获取高亮字的详细数据
  const getWordData = (wordId: string, wordType: string) => {
    if (!passage) return null;

    console.log('getWordData called:', { wordId, wordType });
    
    // 从ID中提取原始ID
    // ID格式: type-originalId-index-counter
    const parts = wordId.split('-');
    let originalId = '';
    
    if (wordType === 'key_word') {
      // key-word-{id}-{index}-{counter}
      originalId = parts.slice(2, -2).join('-');
    } else if (wordType === 'function_word') {
      // function-word-{id}-{index}-{counter}
      originalId = parts.slice(2, -2).join('-');
    } else if (wordType === 'special_char') {
      // special-char-{id}-{index}-{counter}
      originalId = parts.slice(2, -2).join('-');
    } else if (wordType === 'sentence_pattern') {
      // sentence-pattern-{id}-{index}-{counter}
      originalId = parts.slice(2, -2).join('-');
    } else if (wordType === 'word_usage') {
      // word-usage-{id}-{index}-{counter}
      originalId = parts.slice(2, -2).join('-');
    }
    
    // 如果提取失败，尝试直接匹配
    if (!originalId) {
      // 尝试直接匹配ID
      const directMatch = passage.key_words?.find(word => word.id === wordId) ||
                         passage.function_words?.find(word => word.id === wordId) ||
                         passage.special_chars?.find(char => char.id === wordId) ||
                         passage.sentence_patterns?.find(pattern => pattern.id === wordId) ||
                         passage.word_usage?.find(usage => usage.id === wordId);
      if (directMatch) {
        console.log('Direct match found:', directMatch);
        return directMatch;
      }
    }
    
    console.log('Extracted originalId:', originalId);
    
    switch (wordType) {
      case 'key_word':
        const keyWord = passage.key_words.find(word => word.id === originalId);
        console.log('Found key word:', keyWord);
        return keyWord;
      case 'function_word':
        const functionWord = passage.function_words.find(word => word.id === originalId);
        console.log('Found function word:', functionWord);
        return functionWord;
      case 'special_char':
        const specialChar = passage.special_chars?.find(char => char.id === originalId);
        console.log('Found special char:', specialChar);
        return specialChar;
      case 'sentence_pattern':
        const sentencePattern = passage.sentence_patterns?.find(pattern => pattern.id === originalId);
        console.log('Found sentence pattern:', sentencePattern);
        return sentencePattern;
      case 'word_usage':
        const wordUsage = passage.word_usage?.find(usage => usage.id === originalId);
        console.log('Found word usage:', wordUsage);
        return wordUsage;
      default:
        return null;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'partial':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'failed':
        return '提取失败';
      case 'processing':
        return '处理中';
      case 'pending':
        return '等待处理';
      case 'partial':
        return '部分完成';
      default:
        return '草稿';
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !passage) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">加载失败</h3>
          <p className="text-gray-500 mb-6">{error || '文言文材料不存在'}</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => navigate('/classical-chinese')}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              返回列表
            </button>
            <button
              onClick={() => loadPassage()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              重新加载
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/classical-chinese"
              className="text-gray-500 hover:text-gray-700 mr-4"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <BookOpenIcon className="h-8 w-8 mr-3 text-amber-600" />
                {passage.title}
              </h1>
              <div className="mt-2 flex items-center gap-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  passage.processing_status === 'completed' ? 'text-green-600 bg-green-50' :
                  passage.processing_status === 'failed' ? 'text-red-600 bg-red-50' :
                  passage.processing_status === 'processing' ? 'text-blue-600 bg-blue-50' :
                  passage.processing_status === 'pending' ? 'text-yellow-600 bg-yellow-50' :
                  'text-gray-600 dark:text-gray-400 bg-gray-50'
                }`}>
                  {getStatusIcon(passage.processing_status)}
                  <span className="ml-1">{getStatusText(passage.processing_status)}</span>
                </span>
                {passage.is_public === 1 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-blue-600 bg-blue-50">
                    公开分享
                  </span>
                )}
              </div>
            </div>
          </div>
          {passage.processing_status === 'failed' && (
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors disabled:opacity-50"
            >
              {retrying ? (
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <SparklesIcon className="h-4 w-4 mr-2" />
              )}
              重新提取
            </button>
          )}
        </div>
      </div>

      {/* 处理错误信息 */}
      {passage.processing_error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">提取失败</h3>
              <div className="mt-2 text-sm text-red-700">{passage.processing_error}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 左侧：原文展示 + 核心概览 */}
        <div className="space-y-6">
          {/* 原文展示 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2 text-amber-600" />
              文言文原文
            </h2>
            <div className="prose prose-sm max-w-none">
              <HighlightedText
                text={passage.original_text}
                highlights={processedHighlights}
                onWordClick={handleWordClick}
                getWordData={getWordData}
              />
            </div>
          </div>

          {/* 核心概览 - 移动到这里，默认折叠 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection('overview')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <LightBulbIcon className="h-5 w-5 mr-3 text-amber-600" />
                <span className="font-medium text-gray-900 dark:text-gray-100">核心概览</span>
              </div>
              {expandedSections.has('overview') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
            {expandedSections.has('overview') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 全文译文 */}
                {passage.translations.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">全文译文</h4>
                    <div className="text-gray-700 bg-gray-50 p-3 rounded-md">
                      {passage.translations[0]?.translation}
                    </div>
                  </div>
                )}

                {/* 主旨思想 */}
                {passage.main_ideas.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">主旨思想</h4>
                    <div className="text-gray-700">
                      {passage.main_ideas[0]?.main_idea}
                    </div>
                    {passage.main_ideas[0]?.emotion && (
                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <strong>情感：</strong>{passage.main_ideas[0].emotion}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 右侧：知识点展示 - 互斥展开 */}
        <div className="space-y-6">

          {/* 2. 字词精讲 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection('words')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <AcademicCapIcon className="h-5 w-5 mr-3 text-blue-600" />
                <span className="font-medium text-gray-900 dark:text-gray-100">字词精讲</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({passage.key_words.length + passage.function_words.length + passage.special_chars.length} 个)
                </span>
              </div>
              {expandedSections.has('words') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
            {expandedSections.has('words') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 重点实词 */}
                {passage.key_words.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">重点实词</h4>
                    <div className="space-y-2">
                      {passage.key_words.map((word, index) => (
                        <div
                          key={index}
                          id={getWordEntryId('key-word', word.id, index)}
                          className="border-l-4 border-blue-200 pl-3 transition-all duration-300"
                        >
                          <div className="font-medium text-gray-900 dark:text-gray-100">{word.word}</div>
                          <div className="text-sm text-gray-700">{word.meaning_in_text}</div>
                          {word.multiple_meanings && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <strong>一词多义：</strong>{word.multiple_meanings}
                            </div>
                          )}
                          {word.ancient_modern_diff && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <strong>古今异义：</strong>{word.ancient_modern_diff}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 核心虚词 */}
                {passage.function_words.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">核心虚词</h4>
                    <div className="space-y-2">
                      {passage.function_words.map((word, index) => (
                        <div
                          key={index}
                          id={getWordEntryId('function-word', word.id, index)}
                          className="border-l-4 border-green-200 pl-3 transition-all duration-300"
                        >
                          <div className="font-medium text-gray-900 dark:text-gray-100">{word.word}</div>
                          <div className="text-sm text-gray-700">
                            <span className="text-green-600">{word.word_class}</span> - {word.function_description}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 通假字与古今异义 */}
                {passage.special_chars.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">通假字与古今异义</h4>
                    <div className="space-y-2">
                      {passage.special_chars.map((char, index) => (
                        <div key={index} className="border-l-4 border-orange-200 pl-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{char.original_char}</div>
                          <div className="text-sm text-gray-700">
                            {char.char_type === 'tongjiazi' ? '通假字' : '古今异义'}
                            {char.correct_char && ` (本字: ${char.correct_char})`}
                          </div>
                          {char.ancient_meaning && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <strong>古义：</strong>{char.ancient_meaning}
                            </div>
                          )}
                          {char.modern_meaning && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <strong>今义：</strong>{char.modern_meaning}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3. 语法与句式 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection('grammar')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <BeakerIcon className="h-5 w-5 mr-3 text-purple-600" />
                <span className="font-medium text-gray-900 dark:text-gray-100">语法与句式</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({passage.sentence_patterns.length + passage.word_usage.length} 个)
                </span>
              </div>
              {expandedSections.has('grammar') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
            {expandedSections.has('grammar') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 特殊句式 */}
                {passage.sentence_patterns.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">特殊句式</h4>
                    <div className="space-y-3">
                      {passage.sentence_patterns.map((pattern, index) => (
                        <div key={index} className="border-l-4 border-purple-200 pl-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{pattern.pattern_name}</div>
                          <div className="text-sm text-gray-700 mb-2">{pattern.explanation}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 p-2 rounded">
                            <strong>例句：</strong>{pattern.example_sentence}
                          </div>
                          {pattern.normal_order && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <strong>正常语序：</strong>{pattern.normal_order}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 词类活用 */}
                {passage.word_usage.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">词类活用</h4>
                    <div className="space-y-2">
                      {passage.word_usage.map((usage, index) => (
                        <div key={index} className="border-l-4 border-indigo-200 pl-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{usage.word}</div>
                          <div className="text-sm text-gray-700">
                            <span className="text-indigo-600">{usage.usage_type}</span> - {usage.explanation}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 p-2 rounded mt-1">
                            <strong>例句：</strong>{usage.example_sentence}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 4. 文学与文化 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection('literature')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <BookmarkIcon className="h-5 w-5 mr-3 text-red-600" />
                <span className="font-medium text-gray-900 dark:text-gray-100">文学与文化</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({passage.rhetoric.length + passage.literary_knowledge.length + passage.character_analysis.length} 个)
                </span>
              </div>
              {expandedSections.has('literature') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
            {expandedSections.has('literature') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 修辞手法 */}
                {passage.rhetoric.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">修辞手法</h4>
                    <div className="space-y-2">
                      {passage.rhetoric.map((rhetoric, index) => (
                        <div key={index} className="border-l-4 border-red-200 pl-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{rhetoric.rhetoric_type}</div>
                          <div className="text-sm text-gray-700 mb-2">{rhetoric.explanation}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 p-2 rounded">
                            <strong>例句：</strong>{rhetoric.example_sentence}
                          </div>
                          {rhetoric.effect_description && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <strong>表达效果：</strong>{rhetoric.effect_description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 文学常识 */}
                {passage.literary_knowledge.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">文学常识</h4>
                    <div className="space-y-2">
                      {passage.literary_knowledge.map((knowledge, index) => (
                        <div key={index} className="border-l-4 border-yellow-200 pl-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{knowledge.title}</div>
                          <div className="text-sm text-gray-700">{knowledge.content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 人物形象分析 */}
                {passage.character_analysis.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">人物形象分析</h4>
                    <div className="space-y-3">
                      {passage.character_analysis.map((character, index) => (
                        <div key={index} className="border-l-4 border-pink-200 pl-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{character.character_name}</div>
                          <div className="text-sm text-gray-700 mb-1">
                            <strong>性格特点：</strong>{character.personality_traits}
                          </div>
                          <div className="text-sm text-gray-700 mb-1">{character.analysis}</div>
                          {character.supporting_evidence && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              <strong>支撑证据：</strong>{character.supporting_evidence}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 5. 学习与拓展 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection('learning')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <StarIcon className="h-5 w-5 mr-3 text-green-600" />
                <span className="font-medium text-gray-900 dark:text-gray-100">学习与拓展</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({passage.writing_features.length + passage.insights.length} 个)
                </span>
              </div>
              {expandedSections.has('learning') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
            {expandedSections.has('learning') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 写作特色 */}
                {passage.writing_features.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">写作特色</h4>
                    <div className="space-y-2">
                      {passage.writing_features.map((feature, index) => (
                        <div key={index} className="border-l-4 border-green-200 pl-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{feature.title}</div>
                          <div className="text-sm text-gray-700">{feature.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 启示与思考 */}
                {passage.insights.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">启示与思考</h4>
                    <div className="space-y-2">
                      {passage.insights.map((insight, index) => (
                        <div key={index} className="border-l-4 border-emerald-200 pl-3">
                          <div className="text-sm text-gray-700 mb-1">{insight.insight}</div>
                          {insight.modern_significance && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              <strong>现代意义：</strong>{insight.modern_significance}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
